/**
 * Leads module strings (`leads.*`), merged into the dictionary through `extra.ts`.
 * `leadsEn` is typed against `leadsPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const leadsPl = {
  subtitle: 'Lejek pozyskania nowych klientów — przeciągaj karty między etapami.',
  searchPlaceholder: 'Szukaj po nazwie, mieście, kontakcie…',
  newLead: 'Nowy lead',
  board: 'Tablica',
  list: 'Lista',
  allOwners: 'Wszyscy opiekunowie',
  owner: 'Opiekun',
  unassigned: 'Bez opiekuna',
  winRate: 'Konwersja 12 mies.',
  winRateHint: 'Wygrane / (wygrane + utracone) w ostatnich 12 miesiącach',
  pipeline: 'Pipeline',
  pipelineValue: '{amount} / mc',
  openLeads: '{count} otwartych',
  perMonth: '{amount}/mc',
  days: '{count} d',
  daysInStageTitle: '{count} dni w obecnym etapie',
  stale: 'Bez ruchu od ponad 14 dni',
  emptyColumn: 'Brak leadów',
  dropHere: 'Upuść tutaj',
  wonLocked: 'Do „Wygrane” przejdziesz przez „Przekształć w klienta”',
  closedStages: 'Zamknięte',
  expand: 'Rozwiń',
  collapse: 'Zwiń',
  emptyTitle: 'Brak leadów',
  emptyBody: 'Dodaj pierwszego leada, aby zacząć budować lejek.',
  loadError: 'Nie udało się pobrać leadów.',
  stage: {
    NEW: 'Nowy',
    CONTACTED: 'Kontakt',
    QUALIFIED: 'Zakwalifikowany',
    VISIT: 'Wizyta',
    OFFER: 'Oferta',
    APPROVAL: 'Akceptacja',
    WON: 'Wygrany',
    LOST: 'Utracony',
  },
  source: {
    REFERRAL: 'Polecenie',
    COLD_CALL: 'Cold call',
    WEBSITE: 'Strona www',
    TRADE_FAIR: 'Targi',
    SOCIAL: 'Social media',
    PARTNER: 'Partner',
    TENDER: 'Przetarg',
    OTHER: 'Inne',
  },
  col: {
    name: 'Lead',
    city: 'Miasto',
    stage: 'Etap',
    potential: 'Potencjał / mc',
    owner: 'Opiekun',
    source: 'Źródło',
    days: 'W etapie',
    contact: 'Kontakt',
  },
  form: {
    createTitle: 'Nowy lead',
    createDescription: 'Podstaw wystarczy nazwa — resztę uzupełnisz w trakcie rozmów.',
    name: 'Nazwa firmy',
    nip: 'NIP',
    contactName: 'Osoba kontaktowa',
    phone: 'Telefon',
    email: 'E-mail',
    city: 'Miasto',
    source: 'Źródło',
    sourcePlaceholder: 'Wybierz źródło',
    potential: 'Potencjał miesięczny (PLN)',
    notes: 'Notatki',
    owner: 'Opiekun',
    create: 'Dodaj leada',
    created: 'Dodano leada „{name}”.',
    saved: 'Zapisano zmiany.',
    saveError: 'Nie udało się zapisać leada.',
    nameRequired: 'Podaj nazwę firmy.',
    nipInvalid: 'Nieprawidłowy NIP (10 cyfr, suma kontrolna).',
    emailInvalid: 'Podaj poprawny adres e-mail.',
    phoneInvalid: 'Podaj poprawny numer telefonu.',
    potentialInvalid: 'Podaj kwotę większą lub równą 0.',
  },
  lost: {
    title: 'Oznacz leada jako utraconego',
    description: 'Podaj powód — pomoże to ocenić, gdzie tracimy klientów.',
    reason: 'Powód utraty',
    placeholder: 'np. wybrał konkurencję, brak zainteresowania, cena…',
    required: 'Podaj powód utraty leada.',
    confirm: 'Oznacz jako utracony',
  },
  moved: 'Przeniesiono „{name}” do etapu „{stage}”.',
  moveError: 'Nie udało się zmienić etapu — przywrócono poprzedni stan.',
  bulk: { moveTo: 'Przenieś do etapu' },
  undo: 'Cofnij',
  drawer: {
    stageProgress: 'Etap w lejku',
    inStage: 'W etapie od {days}',
    createdAt: 'Utworzono {date}',
    details: 'Dane leada',
    tasks: 'Zadania',
    noTasks: 'Brak zadań powiązanych z leadem.',
    taskDue: 'termin {date}',
    approval: 'Obieg akceptacji',
    openApproval: 'Otwórz wniosek',
    convertedTitle: 'Lead przekształcony w klienta',
    openCustomer: 'Przejdź do klienta',
    lostReason: 'Powód utraty',
    reopen: 'Wznów leada',
    save: 'Zapisz zmiany',
    reset: 'Cofnij',
    notFound: 'Nie znaleziono leada lub brak dostępu.',
    readOnly: 'Podgląd — nie masz uprawnień do edycji leadów.',
    changeStage: 'Zmień etap',
  },
  convert: {
    action: 'Przekształć w klienta',
    title: 'Przekształć w klienta',
    description: 'Lead „{name}” zostanie zapisany jako nowy klient w kartotece.',
    startWorkflow: 'Uruchom obieg akceptacji nowego klienta',
    startWorkflowHint: 'Handlowy (ceny i limit), Logistyka i Zamówienia — wniosek trafi równolegle do trzech działów.',
    noWorkflowRight: 'Nie masz uprawnień do uruchamiania obiegu akceptacji.',
    fillForm: 'Uzupełnij kartę klienta',
    fillFormHint: 'Otworzy się formularz nowego klienta z danymi leada — wniosek wyślesz po jego uzupełnieniu.',
    confirm: 'Przekształć',
    success: 'Utworzono klienta z leada „{name}”.',
    successWorkflow: 'Utworzono klienta i uruchomiono obieg akceptacji.',
    error: 'Nie udało się przekształcić leada.',
  },
};

export const leadsEn: Shape<typeof leadsPl> = {
  subtitle: 'New customer acquisition pipeline — drag cards between stages.',
  searchPlaceholder: 'Search by name, city, contact…',
  newLead: 'New lead',
  board: 'Board',
  list: 'List',
  allOwners: 'All owners',
  owner: 'Owner',
  unassigned: 'Unassigned',
  winRate: '12-month win rate',
  winRateHint: 'Won / (won + lost) over the last 12 months',
  pipeline: 'Pipeline',
  pipelineValue: '{amount} / mo',
  openLeads: '{count} open',
  perMonth: '{amount}/mo',
  days: '{count} d',
  daysInStageTitle: '{count} days in the current stage',
  stale: 'No movement for over 14 days',
  emptyColumn: 'No leads',
  dropHere: 'Drop here',
  wonLocked: 'Use “Convert to customer” to move a lead to Won',
  closedStages: 'Closed',
  expand: 'Expand',
  collapse: 'Collapse',
  emptyTitle: 'No leads',
  emptyBody: 'Add your first lead to start building the pipeline.',
  loadError: 'Could not load leads.',
  stage: {
    NEW: 'New',
    CONTACTED: 'Contacted',
    QUALIFIED: 'Qualified',
    VISIT: 'Visit',
    OFFER: 'Offer',
    APPROVAL: 'Approval',
    WON: 'Won',
    LOST: 'Lost',
  },
  source: {
    REFERRAL: 'Referral',
    COLD_CALL: 'Cold call',
    WEBSITE: 'Website',
    TRADE_FAIR: 'Trade fair',
    SOCIAL: 'Social media',
    PARTNER: 'Partner',
    TENDER: 'Tender',
    OTHER: 'Other',
  },
  col: {
    name: 'Lead',
    city: 'City',
    stage: 'Stage',
    potential: 'Potential / mo',
    owner: 'Owner',
    source: 'Source',
    days: 'In stage',
    contact: 'Contact',
  },
  form: {
    createTitle: 'New lead',
    createDescription: 'A name is enough to start — fill in the rest as the conversations go.',
    name: 'Company name',
    nip: 'Tax ID (NIP)',
    contactName: 'Contact person',
    phone: 'Phone',
    email: 'E-mail',
    city: 'City',
    source: 'Source',
    sourcePlaceholder: 'Select a source',
    potential: 'Monthly potential (PLN)',
    notes: 'Notes',
    owner: 'Owner',
    create: 'Add lead',
    created: 'Lead “{name}” added.',
    saved: 'Changes saved.',
    saveError: 'Could not save the lead.',
    nameRequired: 'Enter the company name.',
    nipInvalid: 'Invalid NIP (10 digits, checksum).',
    emailInvalid: 'Enter a valid e-mail address.',
    phoneInvalid: 'Enter a valid phone number.',
    potentialInvalid: 'Enter an amount greater than or equal to 0.',
  },
  lost: {
    title: 'Mark lead as lost',
    description: 'Give a reason — it helps us understand where we lose customers.',
    reason: 'Reason for loss',
    placeholder: 'e.g. chose a competitor, no interest, price…',
    required: 'Enter the reason for losing the lead.',
    confirm: 'Mark as lost',
  },
  moved: 'Moved “{name}” to “{stage}”.',
  moveError: 'Could not change the stage — the previous state was restored.',
  bulk: { moveTo: 'Move to stage' },
  undo: 'Undo',
  drawer: {
    stageProgress: 'Pipeline stage',
    inStage: 'In stage for {days}',
    createdAt: 'Created {date}',
    details: 'Lead details',
    tasks: 'Tasks',
    noTasks: 'No tasks linked to this lead.',
    taskDue: 'due {date}',
    approval: 'Approval workflow',
    openApproval: 'Open request',
    convertedTitle: 'Lead converted to a customer',
    openCustomer: 'Go to customer',
    lostReason: 'Reason for loss',
    reopen: 'Reopen lead',
    save: 'Save changes',
    reset: 'Reset',
    notFound: 'Lead not found or you do not have access to it.',
    readOnly: 'Read-only — you are not allowed to edit leads.',
    changeStage: 'Change stage',
  },
  convert: {
    action: 'Convert to customer',
    title: 'Convert to customer',
    description: 'Lead “{name}” will be saved as a new customer in the customer file.',
    startWorkflow: 'Start the new-customer approval workflow',
    startWorkflowHint: 'Sales (prices and limit), Logistics and Orders — the request goes to the three departments in parallel.',
    noWorkflowRight: 'You are not allowed to start approval workflows.',
    fillForm: 'Complete the customer form',
    fillFormHint: 'The new-customer form opens prefilled with the lead data — you submit the request once it is complete.',
    confirm: 'Convert',
    success: 'Customer created from lead “{name}”.',
    successWorkflow: 'Customer created and the approval workflow started.',
    error: 'Could not convert the lead.',
  },
};
